/** @prettier */

//-----------------------------
//	Modbus グラフ用データ作成
//-----------------------------

//interface import
import * as apiIF from '../Type/api';
//type import
import {GraphFormType, GraphDataRange} from '../Type/TypeDef';
import {translateDate} from './graphFunction';
import {TFunction} from 'i18next';

interface BasicMakeModbusGraphDataArgIF {
  graphType: GraphFormType; //グラフの種類
  dateRange: GraphDataRange; //グラフ日付範囲
  graphTargetID: string; //グラフ対象のID
  timeZone: string; //タイムゾーン
  trans: TFunction; //翻訳関数
}
interface MakeModbusOUGraphDataArgIF extends BasicMakeModbusGraphDataArgIF {
  modbusStateData:
    | {date: string; states: apiIF.resParameters.modbusOUStates[]}[]
    | null; //熱源機状態データ
  selectedItem: (keyof apiIF.resParameters.modbusOUStates)[]; //グラフ表示項目
}
interface MakeModbusIUGraphDataArgIF extends BasicMakeModbusGraphDataArgIF {
  modbusStateData:
    | {date: string; states: apiIF.resParameters.modbusIUStates[]}[]
    | null; //熱源機状態データ
  selectedItem: (keyof apiIF.resParameters.modbusIUStates)[]; //グラフ表示項目
}

//データが無い場合の戻り値
const resultForNoData = {
  xLabel: [],
  yArrayBar: [],
  yArrayLine: [],
};
/**
 * Modbus グラフ用データ作成
 * @param param0 param0
 * @returns グラフ描画用データ
 */
const makeModbusOUGraphData = ({
  graphType,
  dateRange,
  graphTargetID,
  timeZone,
  modbusStateData,
  selectedItem,
  trans,
}: MakeModbusOUGraphDataArgIF) => {
  //どの空調機が選択された判定
  if (modbusStateData === null || modbusStateData.length === 0) {
    return resultForNoData;
  }
  const targetStateIndex = modbusStateData[0].states.findIndex(state => {
    return state['outdoor-id'] === graphTargetID;
  });
  if (targetStateIndex === -1) {
    return resultForNoData;
  }

  //必要なパラメータを作成
  const {dateformat: dateFormat} = createParameter(graphType, dateRange);

  //グラフ表示に必要なデータのみに絞った配列
  let pickupDate: {
    date: string;
    states: apiIF.resParameters.modbusOUStates[];
  }[] = modbusStateData.reverse().map(data => data); //modbusStateDataのindex番号は終了日のデータが始まっているため逆順で格納する

  //グラフ作成ライブラリが読み取れる形に整形
  let xLabel = [];
  //折れ線グラフの場合
  if (graphType === 'line') {
    let yData = selectedItem.map(item => ({
      name: item,
      data: [] as (number | null)[],
    }));
    for (const stateInfo of pickupDate) {
      //時間情報の追加
      xLabel.push(
        translateDate(stateInfo['date'], timeZone, dateFormat, trans)
      );
      //データ部が存在しない場合
      if (stateInfo.states.length === 0) {
        selectedItem.forEach(item => {
          const index = yData.findIndex(value => value.name === item);
          if (index !== -1) {
            yData[index].data.push(null);
          }
        });
        continue;
      }
      selectedItem.forEach(item => {
        const index = yData.findIndex(value => value.name === item);
        if (index === -1) {
          yData[index].data.push(null);
        } else {
          //データが空欄でないか確認（空欄をグラフ化すると表示がバグる）
          if (
            item in stateInfo.states[targetStateIndex] &&
            stateInfo.states[targetStateIndex][item] !== ''
          ) {
            yData[index].data.push(
              Number(stateInfo.states[targetStateIndex][item])
            );
          } else {
            yData[index].data.push(null);
          }
        }
      });
    }
    return {
      xLabel: xLabel,
      yArrayBar: [],
      yArrayLine: yData,
    };
  }
  //棒グラフの場合
  let yData: (number | null)[] = [];
  for (const stateInfo of pickupDate) {
    //時間情報の追加
    xLabel.push(translateDate(stateInfo['date'], timeZone, dateFormat, trans));
    //データ部が存在しない場合
    if (stateInfo.states.length === 0) {
      yData.push(null);
      continue;
    }
    //データが空欄でないか確認（空欄をグラフ化すると表示がバグる）
    if (
      'power-consumption' in stateInfo.states[targetStateIndex] &&
      stateInfo.states[targetStateIndex]['power-consumption'][
        'cooling-mode'
      ] !== ''
    ) {
      yData.push(
        Math.round(
          Number(
            stateInfo.states[targetStateIndex]['power-consumption'][
              'cooling-mode'
            ]
          ) / 1000
        )
      );
    } else {
      yData.push(null);
    }
  }
  return {
    xLabel: xLabel,
    yArrayBar: [{name: 'power-consumption', data: yData}],
    yArrayLine: [],
  };
};
const makeModbusIUGraphData = ({
  graphType,
  dateRange,
  graphTargetID,
  timeZone,
  modbusStateData,
  selectedItem,
  trans,
}: MakeModbusIUGraphDataArgIF) => {
  //棒グラフが対応していない
  if (graphType === 'bar') {
    return resultForNoData;
  }
  //どの空調機が選択された判定
  if (modbusStateData === null || modbusStateData.length === 0) {
    return resultForNoData;
  }
  const targetStateIndex = modbusStateData[0].states.findIndex(state => {
    return state['unit-id'] === graphTargetID;
  });
  if (targetStateIndex === -1) {
    return resultForNoData;
  }

  //必要なパラメータを作成
  const {dateformat: dateFormat} = createParameter(graphType, dateRange);

  //グラフ表示に必要なデータのみに絞った配列
  let pickupDate: {
    date: string;
    states: apiIF.resParameters.modbusIUStates[];
  }[] = modbusStateData.reverse().map(data => data); //modbusStateDataのindex番号は終了日のデータが始まっているため逆順で格納する

  //グラフ作成ライブラリが読み取れる形に整形
  let xLabel = [];
  let yData = selectedItem.map(item => ({
    name: item,
    data: [] as (number | null)[],
  }));
  //ループ回数記憶用変数
  let counter = 0;
  for (const stateInfo of pickupDate) {
    counter++;
    //範囲設定が1日の場合、y軸の目盛りを少なくするために
    //奇数回のデータ（10分毎のデータ）のみ追加する
    if (dateRange === 'day' && counter % 2 === 0) {
      continue;
    }
    //時間情報の追加
    xLabel.push(translateDate(stateInfo['date'], timeZone, dateFormat, trans));
    //データ部が存在しない場合
    if (stateInfo.states.length === 0) {
      selectedItem.forEach(item => {
        const index = yData.findIndex(value => value.name === item);
        if (index !== -1) {
          yData[index].data.push(null);
        }
      });
      continue;
    }
    selectedItem.forEach(item => {
      const index = yData.findIndex(value => value.name === item);
      if (index === -1) {
        yData[index].data.push(null);
      } else {
        //データが空欄でないか確認（空欄をグラフ化すると表示がバグる）
        if (stateInfo.states[targetStateIndex][item] !== '') {
          yData[index].data.push(
            Number(stateInfo.states[targetStateIndex][item])
          );
        } else {
          yData[index].data.push(null);
        }
      }
    });
  }
  return {
    xLabel: xLabel,
    yArrayBar: [],
    yArrayLine: yData,
  };
};

/**
 * 状態値を検索するために必要なパラメータを作成
 * @param graphType グラフの種類
 * @param dateRange グラフ日付範囲
 * @returns パラメータ
 */
const createParameter = (
  graphType: GraphFormType,
  dateRange: GraphDataRange
) => {
  let distance; //グラフのx軸の刻み
  let range = 24; //検索データ範囲 初期値：1日
  let dateFormat = 'HH'; //グラフの軸表示フォーマット 初期値：1日

  if (graphType === 'line') {
    /* 1日or1週間の切替 */
    if (dateRange === 'day') {
      dateFormat = 'HH:mm';
    } else {
      dateFormat = 'MMM.dd';
    }
  } else {
    /* 期間の切替 */
    /* グラフx軸ラベル */
    switch (dateRange) {
      case 'day':
      default:
        dateFormat = 'HH:mm';
        break;
      case 'week':
        dateFormat = 'MMM.dd';
        break;
      case 'month':
        dateFormat = 'dd';
        break;
      case 'year':
        dateFormat = 'MMM.';
        break;
      case 'tenYear':
        dateFormat = 'yyyy';
        break;
    }
  }
  return {
    dateDistance: distance,
    dateRange: range,
    dateformat: dateFormat,
  };
};

export {makeModbusOUGraphData, makeModbusIUGraphData};
